import React from 'react';
import Cons from '../../utils/Cons';
import { getColumnStyle } from './columnStyle';
import { formatCellValue, formatVietnameseNumber, formatVietnamesePercent, parseLocalizedNumber } from '../../utils/numberFormat';

const parseNumber = (value) => {
    const number = parseLocalizedNumber(value);
    return Number.isFinite(number) ? number : 0;
};

const getTotal = (data, colConf, totalRows) => totalRows.reduce((total, row) => {
    const value = data?.[`${colConf.key} ${row.key}`];
    return total + parseNumber(value);
}, 0);

const evaluateArithmetic = (expression) => {
    const tokens = expression.match(/\d*\.?\d+|[()+\-*/]/g) || [];
    let position = 0;

    const parsePrimary = () => {
        const token = tokens[position++];
        if (token === '(') {
            const value = parseExpression();
            if (tokens[position++] !== ')') throw new Error('Missing closing parenthesis');
            return value;
        }
        if (token === '+') return parsePrimary();
        if (token === '-') return -parsePrimary();
        const value = Number(token);
        if (!Number.isFinite(value)) throw new Error('Invalid number in column formula');
        return value;
    };

    const parseTerm = () => {
        let value = parsePrimary();
        while (tokens[position] === '*' || tokens[position] === '/') {
            const operator = tokens[position++];
            const right = parsePrimary();
            value = operator === '*' ? value * right : value / right;
        }
        return value;
    };

    const parseExpression = () => {
        let value = parseTerm();
        while (tokens[position] === '+' || tokens[position] === '-') {
            const operator = tokens[position++];
            const right = parseTerm();
            value = operator === '+' ? value + right : value - right;
        }
        return value;
    };

    const result = parseExpression();
    if (position !== tokens.length || !Number.isFinite(result)) return 0;
    return result;
};

const getTotalPercent = (data, colConf, colConfArray, totalRows) => {
    const formula = colConf.func?.replace(/^p/, '');
    if (!formula) return getTotal(data, colConf, totalRows);

    const columnsByCode = new Map(colConfArray.map(column => [column.code, column]));
    const aggregateFormula = formula.replace(/\{([^}]+)\}/g, (match, code) => {
        const sourceColumn = columnsByCode.get(code.trim());
        if (!sourceColumn) return '0';
        return String(getTotal(data, sourceColumn, totalRows));
    });
    return evaluateArithmetic(aggregateFormula);
};

const getDirectPermissionRows = (rowConfig, userName) => {
    if (!rowConfig || !userName) return [];

    const accountKey = userName.toLocaleLowerCase('en-US');
    const hasAccount = node => (node?.forAccounts || [])
        .some(account => account.toLocaleLowerCase('en-US') === accountKey);
    const topLevelRows = rowConfig.child || [];

    const selectedRows = [];
    const rootHasAccount = hasAccount(rowConfig);

    if (rootHasAccount) {
        const flattenRows = (nodes, prefix = '') => {
            const rows = [];
            (nodes || []).forEach((node, index) => {
                const code = prefix ? `${prefix}.${index}` : String(index);
                rows.push({ ...node, code });
                rows.push(...flattenRows(node.child, code));
            });
            return rows;
        };
        const rowsByCode = new Map(flattenRows(rowConfig.child).map(row => [row.code, row]));
        return topLevelRows.map((_, index) => rowsByCode.get(String(index))).filter(Boolean);
    }

    topLevelRows.forEach((topNode, topIndex) => {
        const topCode = String(topIndex);
        const directRows = [];
        const collectDirectRows = (node, code) => {
            if (hasAccount(node)) directRows.push({ node, code });
            (node.child || []).forEach((child, childIndex) => {
                collectDirectRows(child, `${code}.${childIndex}`);
            });
        };
        collectDirectRows(topNode, topCode);
        if (directRows.length === 0) {
            return;
        }

        const directCodes = new Set(directRows.map(row => row.code));
        const lowestDirectRows = directRows.filter(({ code }) => {
            for (const candidate of directCodes) {
                if (candidate.startsWith(`${code}.`)) return false;
            }
            return true;
        });
        selectedRows.push(...lowestDirectRows.map(row => row.code));
    });

    const flattenRows = (nodes, prefix = '') => {
        const rows = [];
        (nodes || []).forEach((node, index) => {
            const code = prefix ? `${prefix}.${index}` : String(index);
            rows.push({ ...node, code });
            rows.push(...flattenRows(node.child, code));
        });
        return rows;
    };
    const flattenedRows = flattenRows(rowConfig.child);
    const flattenedByCode = new Map(flattenedRows.map(row => [row.code, row]));
    return selectedRows.map(code => flattenedByCode.get(code)).filter(Boolean);
};

const formatValue = (value, colConf) => {
    if (colConf.cType === Cons.C_TYPE[0]) return '';
    if (colConf.func?.includes('p')) return formatCellValue(value, formatVietnamesePercent);
    return formatCellValue(value, formatVietnameseNumber);
};

const TotalRow = ({ data, colConfArray, rowConfArray, rowConfig, userName, colConfig }) => {
    const totalRows = getDirectPermissionRows(rowConfig, userName);
    const rowsToSum = totalRows.length
        ? totalRows
        : rowConfArray.filter(row => row.code && row.code.split('.').length === 1);
    const widths = colConfig?.widths || [];

    return (
        <tr className='total-row'>
            <th className='stick-column' style={{ left: '0px' }} />
            <th className='code stick-column' style={{ left: `${(widths[0] || 0) / 3}rem` }} />
            <th className='pb-name stick-column' style={{ left: `${(widths[0] || 0) * 4 / 3}rem` }}>
                TỔNG CỘNG
            </th>
            {colConfArray.map((colConf, index) => {
                const value = colConf.func?.startsWith('p')
                    ? getTotalPercent(data, colConf, colConfArray, rowsToSum)
                    : getTotal(data, colConf, rowsToSum);
                return (
                    <td
                        key={`total-${colConf.key}`}
                        className='data-cell text-center'
                        style={getColumnStyle(colConf, index, colConfig)}>
                        {formatValue(value, colConf)}
                    </td>
                );
            })}
        </tr>
    );
};

export default TotalRow;
